import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Bitcoin } from "lucide-react";
import Seo from "@/components/Seo";
import SiteNav from "@/components/SiteNav";
import ComplianceFooter from "@/components/ComplianceFooter";
import { api, formatApiError } from "@/lib/api";

const PRODUCTS = [
  {
    key: "boutique_tee",
    name: "Signature Tee",
    price: "$65",
    desc: "Heavyweight combed cotton in bone white, gold Society monogram at the chest.",
    img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Plain white heavyweight t-shirt",
  },
  {
    key: "boutique_hoodie",
    name: "Noir Hoodie",
    price: "$145",
    desc: "Brushed-fleece hoodie in midnight black with tonal embroidery. For off-duty discretion.",
    img: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Person wearing a grey hooded sweatshirt",
  },
  {
    key: "boutique_cap",
    name: "Society Cap",
    price: "$55",
    desc: "Washed-black six-panel cap, gold serif wordmark. Understated, unmistakable.",
    img: "https://images.unsplash.com/photo-1521369909029-2afed882baee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    alt: "Washed black baseball cap",
  },
];

export default function Boutique() {
  const [busy, setBusy] = useState("");

  const buy = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      let userId;
      try {
        const { data } = await api.get("/auth/me");
        userId = data.id;
      } catch { /* guest checkout is fine for merch */ }
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        user_id: userId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy("");
    }
  };

  return (
    <div className="bg-obsidian min-h-screen">
      <Seo title="The Boutique — Monroe Society" siteName="Monroe Society" description="Official Monroe Society merchandise — tees, hoodies, and caps carrying the Society mark." />
      <SiteNav />
      <div className="relative h-72 sm:h-96 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1611323340350-bdcc0e6cfae5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Champagne served in a dimly lit luxury lounge"
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/20" />
        <div className="absolute inset-0 flex items-end">
          <motion.div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-12 w-full" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}>
            <p className="text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">The Boutique — Official Monroe Society Merchandise</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-amber-100/95">
              Wear the <span className="italic gold-text">Society</span>
            </h1>
          </motion.div>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-16 pb-24">
        <p className="max-w-xl text-base text-neutral-400 font-light leading-relaxed mb-14">
          Quiet branding for those who know. The launch collection carries the Monroe Society mark only —
          final designs are in production; photography shown is illustrative of the blank garments.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.map((p, i) => (
            <motion.div
              key={p.key}
              data-testid={`product-card-${p.key}`}
              className="group bg-onyx border hairline-gold card-glow transition-all duration-500 overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0, transition: { delay: i * 0.1 } }}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[25%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              <div className="p-6">
                <div className="flex items-baseline justify-between mb-2">
                  <h2 className="font-serif text-2xl text-amber-100/95">{p.name}</h2>
                  <span className="font-serif text-xl gold-text">{p.price}</span>
                </div>
                <p className="text-sm text-neutral-400 font-light leading-relaxed mb-6">{p.desc}</p>
                <button
                  data-testid={`buy-${p.key}`}
                  onClick={() => buy(p.key)}
                  disabled={!!busy}
                  className="w-full py-3.5 bg-gold text-obsidian text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-bright hover:shadow-[0_0_25px_rgba(212,175,55,0.25)] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <ShoppingBag size={14} />
                  {busy === p.key ? "Preparing Checkout…" : "Purchase"}
                </button>
                <button
                  disabled
                  data-testid={`crypto-buy-${p.key}`}
                  className="mt-3 w-full py-3 border border-dashed hairline-gold text-[11px] uppercase tracking-[0.15em] text-neutral-500 cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Bitcoin size={13} className="text-gold/50" />
                  Bitcoin — Coming Soon
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-xs text-neutral-600 max-w-lg leading-relaxed">
          Secure, discreetly billed checkout. All sales final per our Terms of Service.
          Ships discreetly in unmarked packaging.
        </p>

        <div className="mt-20 border-t hairline-gold pt-14">
          <p className="text-xs uppercase tracking-[0.35em] text-gold/80 mb-8">Arriving With the Launch Collection</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { name: "Noir Poster Series", desc: "Gallery-stock prints of the Society's launch campaign — gold on obsidian." },
              { name: "Society Sticker Suite", desc: "Embossed monogram seals and wordmark decals for those in the know." },
            ].map((a) => (
              <div key={a.name} data-testid={`arriving-${a.name.toLowerCase().replace(/\s+/g, "-")}`} className="p-8 bg-onyx border border-dashed hairline-gold">
                <p className="font-serif text-2xl text-amber-100/90 mb-2">{a.name}</p>
                <p className="text-sm text-neutral-500 font-light leading-relaxed mb-5">{a.desc}</p>
                <span className="text-[10px] uppercase tracking-[0.25em] text-gold/60">Design in production</span>
              </div>
            ))}
          </div>
        </div>
      </main>
      <ComplianceFooter />
    </div>
  );
}
